import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { areaNum } from "@/components/shared";
import { SetupDashboard } from "@/components/SetupDashboard";
import { ArrowLeft, Ruler, Banknote, Boxes, RefreshCcw, LayoutDashboard } from "lucide-react";

const SOLD_LABEL = "Teklif tutarı girilmiş projeler";

const ZoneKpiCard = ({ icon: Icon, label, zones, render, sub, accent, testid, globalSel }) => {
  const opts = ["all", ...zones];
  const [zi, setZi] = useState(0);
  useEffect(() => {
    if (globalSel) {
      const i = opts.indexOf(globalSel.zone);
      if (i >= 0) setZi(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSel]);
  const zone = opts[zi % opts.length];
  return (
    <div className="bg-white border border-slate-200 p-5 relative" data-testid={testid}>
      {opts.length > 1 && (
        <button
          type="button"
          onClick={() => setZi((z) => (z + 1) % opts.length)}
          data-testid={`${testid}-toggle`}
          title="Bölge değiştir"
          className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 border border-slate-200 text-slate-500 hover:border-slate-900 hover:text-slate-900 transition-colors duration-150"
        >
          <RefreshCcw size={11} /> {zone === "all" ? "Tümü" : zone}
        </button>
      )}
      <div className={`w-10 h-10 flex items-center justify-center ${accent}`}><Icon size={18} /></div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-4">{label}</div>
      <div className="font-heading text-3xl font-extrabold tracking-tight tabular-nums mt-1 break-words" data-testid={`${testid}-value`}>{render(zone)}</div>
      {sub && <div className="mt-2 text-xs text-slate-500 tabular-nums space-y-0.5" data-testid={`${testid}-sub`}>{sub(zone)}</div>}
    </div>
  );
};

export default function AssignerDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [globalSel, setGlobalSel] = useState(null);
  const myZones = user?.zones || [];

  useEffect(() => {
    api.get("/assigner/projects").then((r) => { setRows(r.data); setLoaded(true); }).catch(() => setLoaded(true));
    api.get("/assigner/rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  const fmt = (n, d = 0) => n.toLocaleString("tr-TR", { maximumFractionDigits: d });
  const inZone = (zone) => rows.filter((p) => zone === "all" || p.zone === zone);
  const sold = (zone) => inZone(zone).filter((p) => Number(p.offer_amount) > 0);
  const soldArea = (zone) => fmt(sold(zone).reduce((s, p) => s + areaNum(p.area_m2), 0), 1);
  const isOzel = (p) => p.project_status === "Özel";
  const splitSub = (list, valFn, suffix = "") => {
    const ozel = list.filter(isOzel);
    const sbb = list.filter((p) => !isOzel(p));
    return <div>Özel: {fmt(valFn(ozel), 1)}{suffix} · SBB: {fmt(valFn(sbb), 1)}{suffix}</div>;
  };
  const ciroTotals = (zone, ps) => {
    const t = {};
    sold(zone).filter((p) => !ps || (ps === "Özel" ? isOzel(p) : !isOzel(p))).forEach((p) => {
      if (p.offer_amount > 0) {
        const c = p.offer_currency || "TRY";
        t[c] = (t[c] || 0) + p.offer_amount;
      }
    });
    return t;
  };
  const ciroTL = (zone, ps) => {
    const t = ciroTotals(zone, ps);
    if (!rates?.USD || !rates?.EUR) {
      const parts = ["TRY", "USD", "EUR"].filter((c) => t[c]).map((c) => `${fmt(t[c])} ${c === "TRY" ? "TL" : c}`);
      return parts.length ? parts.join(" · ") : "0";
    }
    const tl = (t.TRY || 0) + (t.USD || 0) * rates.USD + (t.EUR || 0) * rates.EUR;
    return `₺${fmt(tl)}`;
  };
  const ciroSub = (zone, ps) => {
    const t = ciroTotals(zone, ps);
    const parts = ["USD", "EUR", "TRY"].filter((c) => t[c]).map((c) => `${fmt(t[c])} ${c === "TRY" ? "TL" : c}`);
    return (
      <>
        <div>{parts.length ? parts.join(" · ") : "Döviz bazında kayıt yok"}</div>
        {rates?.USD && rates?.EUR && (
          <div className="text-[10px] text-slate-400">
            TCMB {rates.date}: $ {fmt(rates.USD, 4)} · € {fmt(rates.EUR, 4)}
          </div>
        )}
      </>
    );
  };

  if (user?.account_kind === "restricted") {
    return (
      <div className="min-h-screen bg-[#F5F7FA]" data-testid="member-dashboard-page">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><LayoutDashboard size={10} /> Dashboard</div>
            </div>
            <button onClick={() => navigate("/atayici")} data-testid="assigner-dashboard-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150">
              <ArrowLeft size={14} /> Geri Dön
            </button>
          </div>
        </header>
        <main className="max-w-[1000px] mx-auto px-4 sm:px-6 py-8 space-y-6">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">{loaded ? `Size atanmış ${rows.length} proje` : "Yükleniyor..."}</p>
          </div>
          <SetupDashboard rows={rows} testidPrefix="member" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="assigner-dashboard-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><LayoutDashboard size={10} /> Atayıcı Dashboard</div>
          </div>
          <button
            onClick={() => navigate("/atayici")}
            data-testid="assigner-dashboard-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150"
          >
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            {loaded ? `${rows.length} proje · Satış ölçütü: "${SOLD_LABEL}"` : "Yükleniyor..."} — kartların sağ üst köşesinden bölge değiştirebilirsiniz
          </p>
          <div className="flex items-center gap-2 mt-4">
            {["all", ...myZones].map((z) => (
              <button
                key={z}
                type="button"
                data-testid={`dash-global-zone-${z === "all" ? "all" : z.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setGlobalSel({ zone: z, n: Date.now() })}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border transition-colors duration-150 ${
                  globalSel?.zone === z
                    ? z === "Blue Zone"
                      ? "bg-blue-600 border-blue-600 text-white"
                      : z === "Green Zone"
                        ? "bg-emerald-600 border-emerald-600 text-white"
                        : "bg-slate-900 border-slate-900 text-white"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900"
                }`}
              >
                {z === "all" ? "Tümü" : z}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assigner-dashboard-cards">
          <ZoneKpiCard
            icon={Boxes}
            label="Stand Sayısı"
            zones={myZones}
            globalSel={globalSel}
            accent="bg-blue-500/10 text-blue-600"
            testid="dash-stand-count"
            render={(zone) => inZone(zone).length}
            sub={(zone) => splitSub(inZone(zone), (l) => l.length)}
          />
          <ZoneKpiCard
            icon={Ruler}
            label="Toplam Satılan Alan (m²)"
            zones={myZones}
            globalSel={globalSel}
            accent="bg-emerald-500/10 text-emerald-600"
            testid="dash-sold-area"
            render={soldArea}
            sub={(zone) => splitSub(sold(zone), (l) => l.reduce((s, p) => s + areaNum(p.area_m2), 0), " m²")}
          />
          <ZoneKpiCard
            icon={Ruler}
            label="Toplam Alan (m²) — Tüm Projeler"
            zones={myZones}
            globalSel={globalSel}
            accent="bg-teal-500/10 text-teal-600"
            testid="dash-total-area"
            render={(zone) => fmt(inZone(zone).reduce((s, p) => s + areaNum(p.area_m2), 0), 1)}
            sub={(zone) => splitSub(inZone(zone), (l) => l.reduce((s, p) => s + areaNum(p.area_m2), 0), " m²")}
          />
          <ZoneKpiCard
            icon={Banknote}
            label="Toplam Ciro (TL)"
            zones={myZones}
            globalSel={globalSel}
            accent="bg-amber-500/10 text-amber-600"
            testid="dash-revenue"
            render={ciroTL}
            sub={ciroSub}
          />
          <ZoneKpiCard
            icon={Banknote}
            label="Özel Ciro (TL)"
            zones={myZones}
            globalSel={globalSel}
            accent="bg-violet-500/10 text-violet-600"
            testid="dash-revenue-ozel"
            render={(zone) => ciroTL(zone, "Özel")}
            sub={(zone) => ciroSub(zone, "Özel")}
          />
          <ZoneKpiCard
            icon={Banknote}
            label="SBB Ciro (TL)"
            zones={myZones}
            globalSel={globalSel}
            accent="bg-sky-500/10 text-sky-600"
            testid="dash-revenue-sbb"
            render={(zone) => ciroTL(zone, "SBB")}
            sub={(zone) => ciroSub(zone, "SBB")}
          />
        </div>
      </main>
    </div>
  );
}
